import type { UseSomaOptions, SomaMessage, SomaStreamEvent } from '../types'

export interface SomaCoreClientState {
  messages: SomaMessage[]
  isConnected: boolean
  isStreaming: boolean
  streamContent: string
}

export type SomaStateChangeListener = (state: SomaCoreClientState) => void

export class SomaCoreClient {
  private options: UseSomaOptions
  private ws: WebSocket | null = null
  private ready = false
  private pending: string[] = []

  public messages: SomaMessage[] = []
  public isConnected = false
  public isStreaming = false
  public streamContent = ''

  private streamRef = ''
  private contentRef = ''
  private thinkingRef = ''
  private historyLoaded = false
  private listeners: Set<SomaStateChangeListener> = new Set()

  constructor(options: UseSomaOptions) {
    this.options = options
  }

  public subscribe(listener: SomaStateChangeListener): () => void {
    this.listeners.add(listener)
    listener(this.getState())
    return () => {
      this.listeners.delete(listener)
    }
  }

  public getState(): SomaCoreClientState {
    return {
      messages: [...this.messages],
      isConnected: this.isConnected,
      isStreaming: this.isStreaming,
      streamContent: this.streamContent,
    }
  }

  private notify() {
    const state = this.getState()
    for (const listener of this.listeners) {
      listener(state)
    }
  }

  public updateOptions(newOptions: Partial<UseSomaOptions>) {
    this.options = { ...this.options, ...newOptions }
  }

  /**
   * Auth headers for REST calls.
   *
   * Prefer the `authHeaders` factory when provided (it is the documented escape
   * hatch for custom auth, e.g. `x-api-key`). Otherwise fall back to the legacy
   * `Authorization: Bearer <apiKey>` used by the rest of the core client.
   */
  private restHeaders(): Record<string, string> {
    if (this.options.authHeaders) return this.options.authHeaders()
    const { apiKey } = this.options
    return apiKey ? { Authorization: `Bearer ${apiKey}` } : {}
  }

  private get wsUrl(): string {
    const { baseUrl = '', wsPath = '/agent-ws' } = this.options
    if (baseUrl) {
      const proto = baseUrl.startsWith('https') ? 'wss' : 'ws'
      const cleanHost = baseUrl.replace(/^https?:\/\//, '')
      return `${proto}://${cleanHost}${wsPath}`
    }
    if (typeof window !== 'undefined') {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
      return `${proto}://${window.location.host}${wsPath}`
    }
    return `ws://localhost:4000${wsPath}`
  }

  public async loadHistory(): Promise<void> {
    const { agentId, conversationId = `dm:${agentId}`, apiKey, baseUrl = '' } = this.options
    if (this.historyLoaded || !apiKey || !conversationId) return
    this.historyLoaded = true

    const api = baseUrl ? `${baseUrl}/api/conversations/${encodeURIComponent(conversationId)}` : ''
    if (!api) return

    try {
      const res = await fetch(api, {
        headers: { Authorization: `Bearer ${apiKey}` },
      })
      if (res.ok) {
        const data = await res.json()
        if (data.messages?.length) {
          this.messages = data.messages.map((m: any) => ({
            id: m.id || crypto.randomUUID(),
            role: m.role,
            content: m.content,
            thinking: m.thinking,
            tools: m.tools,
            timestamp: new Date(m.timestamp || Date.now()),
          }))
          this.notify()
        }
      }
    } catch {
      /* offline or backend unavailable */
    }
  }

  /**
   * Delete a conversation on the backend (soft-delete).
   * Maps to `DELETE /api/conversations/:id`.
   *
   * @param conversationId optional — defaults to the client's `conversationId`.
   * @returns true if the backend confirmed the deletion, false otherwise.
   *
   * @remarks
   * This clears the local session state (messages + any in-flight stream/pending
   * buffers). It does NOT close the WebSocket or change the active `cid` — the
   * server may still append to the deleted conversation until you reconnect. If
   * you need to "delete and start fresh", call `disconnect()` and then
   * `updateOptions({ conversationId: '<new-id>' })` before `connect()`.
   */
  public async deleteConversation(conversationId?: string): Promise<boolean> {
    const { agentId, conversationId: defaultCid = `dm:${agentId}`, baseUrl = '' } = this.options
    const id = conversationId || defaultCid
    if (!baseUrl || !id) return false

    try {
      const res = await fetch(`${baseUrl}/api/conversations/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: this.restHeaders(),
      })
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          this.options.onAuthError?.(res.status)
        }
        return false
      }
      this.resetSession()
      this.notify()
      return true
    } catch {
      return false
    }
  }

  /**
   * Reset local state after a successful delete: clears messages, history flag,
   * in-flight stream buffers, streaming flag and queued (pending) prompts.
   */
  private resetSession(): void {
    this.messages = []
    this.historyLoaded = false
    this.streamRef = ''
    this.contentRef = ''
    this.thinkingRef = ''
    this.streamContent = ''
    this.isStreaming = false
    this.pending = []
  }

  public connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return

    this.loadHistory()

    const { agentId, conversationId = `dm:${agentId}`, apiKey } = this.options
    const ws = new WebSocket(this.wsUrl)
    ws.binaryType = 'arraybuffer'

    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          type: 'init',
          uid: agentId,
          cid: conversationId,
          token: apiKey || '',
        })
      )
    }

    ws.onmessage = (event) => {
      try {
        let raw: string
        if (typeof event.data === 'string') {
          raw = event.data
        } else if (event.data instanceof ArrayBuffer) {
          raw = new TextDecoder().decode(event.data)
        } else if (event.data instanceof Blob) {
          event.data
            .text()
            .then((text: string) => this.processMessage(text, ws))
            .catch(() => {})
          return
        } else {
          return
        }
        this.processMessage(raw, ws)
      } catch (e) {
        console.error('[SomaCoreClient] onmessage error:', e)
      }
    }

    ws.onclose = () => {
      this.isConnected = false
      this.ready = false
      this.notify()
    }

    ws.onerror = () => {
      this.options.onError?.('Connection error')
    }

    this.ws = ws
  }

  private processMessage(raw: string, ws: WebSocket) {
    const d: SomaStreamEvent = JSON.parse(raw)
    switch (d.type) {
      case 'ready':
        this.ready = true
        this.isConnected = true
        this.notify()
        for (const text of this.pending) {
          ws.send(JSON.stringify({ type: 'prompt', text }))
        }
        this.pending = []
        break

      case 'thinking_start':
        this.isStreaming = true
        this.thinkingRef = ''
        this.notify()
        break

      case 'thinking':
        this.thinkingRef += d.text
        this.options.onThinking?.(d.text)
        this.notify()
        break

      case 'thinking_end':
        break

      case 'delta':
        this.streamRef += d.text
        this.contentRef = this.streamRef
        this.streamContent = this.streamRef
        this.options.onDelta?.(d.text)
        this.notify()
        break

      case 'tool':
        this.options.onTool?.(d.name, d.input)
        break

      case 'done': {
        this.isStreaming = false
        const content = this.contentRef || this.streamRef
        const thinking = this.thinkingRef.trim() || undefined

        if (content || thinking) {
          this.messages.push({
            id: crypto.randomUUID(),
            role: 'assistant',
            content: content || '(sin respuesta)',
            thinking,
            timestamp: new Date(),
          })
          this.contentRef = ''
          this.streamRef = ''
          this.thinkingRef = ''
          this.streamContent = ''
        }
        this.notify()
        this.options.onDone?.()
        break
      }

      case 'cancelled': {
        this.isStreaming = false
        const content = this.contentRef || this.streamRef || ''
        if (content) {
          this.messages.push({
            id: crypto.randomUUID(),
            role: 'assistant',
            content: content + '\n\n_⏹️ Cancelado_',
            thinking: this.thinkingRef.trim() || undefined,
            timestamp: new Date(),
          })
          this.contentRef = ''
          this.streamRef = ''
          this.thinkingRef = ''
          this.streamContent = ''
        }
        this.notify()
        this.options.onCancelled?.()
        break
      }

      case 'error':
        this.isStreaming = false
        this.notify()
        this.options.onError?.(d.message)
        break
    }
  }

  public send(text: string): void {
    this.messages.push({
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    })
    this.notify()

    if (this.ready && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'prompt', text }))
    } else {
      this.pending.push(text)
      this.connect()
    }
  }

  public cancel(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'cancel' }))
    }
  }

  public reconnect(): void {
    this.disconnect()
    this.connect()
  }

  public disconnect(): void {
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.ready = false
    this.isConnected = false
    this.notify()
  }
}
